import {
  purchaseOrderQuery,
  purchaseOrdersQuery,
  useExportPurchaseOrderPdfMutation,
} from "@common/api/purchase-orders"
import type {
  Currency,
  PurchaseOrderDetailPublic,
  PurchaseOrderItemDetailPublic,
} from "@common/client"
import { DropdownMenuItem } from "@common/components/ui/dropdown-menu"
import { useEscape } from "@common/hooks/use-escape"
import { formatCurrency } from "@common/lib/currency"
import { formatDate } from "@common/lib/date-utils"
import { formatDecimal } from "@common/lib/number"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Download } from "lucide-react"
import {
  DetailPane,
  DetailPanelFields,
  PaneMenu,
  PaneNumber,
} from "@/components/layout/detail-panel"
import {
  PaneChip,
  PaneSection,
  PaneSections,
  PaneStepper,
  PaneTitle,
} from "@/components/page/pane"
import useCustomToast from "@/hooks/use-custom-toast"
import { downloadBlob } from "@/lib/download"
import { PURCHASE_ORDER_LIST } from "./-lib/order"

const ITEM_ROW = "grid grid-cols-[minmax(0,1fr)_auto] gap-3 py-1"

const MUTED_LINE = "truncate text-small-regular text-muted-foreground"

export const Route = createFileRoute(
  "/_layout/inventory_/purchase-orders/$purchaseOrderId",
)({
  loader: ({ context: { queryClient }, params: { purchaseOrderId } }) =>
    queryClient.ensureQueryData(
      purchaseOrderQuery({ purchase_order_id: purchaseOrderId }),
    ),
  component: PurchaseOrderPane,
})

function PurchaseOrderPane(): React.ReactElement {
  const { purchaseOrderId } = Route.useParams()
  const { data: purchaseOrder } = useSuspenseQuery(
    purchaseOrderQuery({ purchase_order_id: purchaseOrderId }),
  )
  const { data: list } = useSuspenseQuery(
    purchaseOrdersQuery(PURCHASE_ORDER_LIST),
  )
  const navigate = useNavigate()
  const close = () => navigate({ to: "/inventory/purchase-orders" })
  useEscape(close)
  const index = list.data.findIndex(({ id }) => id === purchaseOrderId)
  const step = (offset: number) => {
    const target = index === -1 ? undefined : list.data[index + offset]
    if (target) {
      void navigate({
        to: "/inventory/purchase-orders/$purchaseOrderId",
        params: { purchaseOrderId: target.id },
      })
    }
  }

  return (
    <DetailPane
      title={purchaseOrder.number}
      heading={<PaneNumber>{purchaseOrder.number}</PaneNumber>}
      actions={
        <>
          <PaneStepper
            noun="purchase order"
            index={index}
            total={list.data.length}
            onPrevious={index > 0 ? () => step(-1) : null}
            onNext={
              index !== -1 && index < list.data.length - 1
                ? () => step(1)
                : null
            }
          />
          <PurchaseOrderMenu purchaseOrder={purchaseOrder} />
        </>
      }
      onClose={close}
    >
      <DetailPanelFields>
        <PaneSections header={<OrderSummary purchaseOrder={purchaseOrder} />}>
          <OrderItems purchaseOrder={purchaseOrder} />
          {purchaseOrder.notes && (
            <PaneSection title="Notes">
              <p className="whitespace-pre-wrap text-body-regular">
                {purchaseOrder.notes}
              </p>
            </PaneSection>
          )}
        </PaneSections>
      </DetailPanelFields>
    </DetailPane>
  )
}

function PurchaseOrderMenu({
  purchaseOrder,
}: {
  purchaseOrder: PurchaseOrderDetailPublic
}): React.ReactElement {
  const mutation = useExportPurchaseOrderPdfMutation()
  const showToast = useCustomToast()

  const exportPdf = () =>
    mutation.mutate(
      { purchase_order_id: purchaseOrder.id },
      {
        onSuccess: (content) => {
          const blob = content instanceof Blob ? content : new Blob([content])
          downloadBlob(blob, `${purchaseOrder.number}.pdf`)
          showToast(
            "PDF exported",
            `${purchaseOrder.number} downloaded.`,
            "success",
          )
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <PaneMenu label="Purchase order actions">
      <DropdownMenuItem disabled={mutation.isPending} onSelect={exportPdf}>
        <Download />
        Export PDF
      </DropdownMenuItem>
    </PaneMenu>
  )
}

function OrderSummary({
  purchaseOrder,
}: {
  purchaseOrder: PurchaseOrderDetailPublic
}): React.ReactElement {
  return (
    <PaneTitle title={purchaseOrder.vendor?.name ?? "Purchase order"}>
      <div className="flex flex-wrap items-center gap-1.5">
        <PaneChip>
          <span className="text-muted-foreground">Ordered</span>
          {formatDate(purchaseOrder.ordered_on)}
        </PaneChip>
        {purchaseOrder.expected_on && (
          <PaneChip>
            <span className="text-muted-foreground">Expected</span>
            {formatDate(purchaseOrder.expected_on)}
          </PaneChip>
        )}
        {purchaseOrder.total !== null && (
          <PaneChip>
            <span className="text-muted-foreground">Total</span>
            {formatCurrency(purchaseOrder.total, purchaseOrder.currency)}
          </PaneChip>
        )}
      </div>
    </PaneTitle>
  )
}

function OrderItems({
  purchaseOrder,
}: {
  purchaseOrder: PurchaseOrderDetailPublic
}): React.ReactElement {
  return (
    <PaneSection title="Parts" meta={purchaseOrder.items.length}>
      {purchaseOrder.items.map((item) => (
        <ItemRow key={item.id} item={item} currency={purchaseOrder.currency} />
      ))}
    </PaneSection>
  )
}

function ItemRow({
  item,
  currency,
}: {
  item: PurchaseOrderItemDetailPublic
  currency: Currency
}): React.ReactElement {
  const quantity = formatDecimal(item.quantity, 3)

  return (
    <div className={ITEM_ROW}>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-body-medium">{item.part?.number}</span>
        <span className={MUTED_LINE}>{item.part?.description}</span>
      </span>
      <span className="flex flex-col items-end tabular-nums">
        <span className="text-body-regular">
          {item.unit_price === null
            ? quantity
            : `${quantity} × ${formatCurrency(item.unit_price, currency)}`}
        </span>
        {item.unit_price !== null && (
          <span className={MUTED_LINE}>
            {formatCurrency(
              Number(item.quantity) * Number(item.unit_price),
              currency,
            )}
          </span>
        )}
      </span>
    </div>
  )
}
